<template>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate
        client-dropdown=""
        sub-client-id=""
        report-sick-days-id=""
        submit-form
        reported-sick-days-id="">
    <div class="row">
      <div class="col-lg-2 col-md-6 col-sm-12">
        <Datepicker
            v-model="formData.startDate"
            position="down"
            :label="$t('general.start_date')"
            :placeholder="$t('general.start_date')"
            :validation="v$.startDate"
            :messages="v$.startDate.required.$message"/>
      </div>
      <div class="col-lg-2 col-md-6 col-sm-12">
        <Datepicker
            v-model="formData.endDate"
            position="down"
            :label="$t('general.end_date')"
            :placeholder="$t('general.end_date')"/>
      </div>
      <div class="col-lg-2 col-md-6 col-sm-12">
        <BaseCheckbox
            v-model="formData.doctorsAppointmentMade"
            label="Arzttermin vereinbart"
            @update:modelValue="onDoctorsAppointmentChange"/>

        <span v-if="formData.doctorsAppointmentMade">
           <Datepicker
               v-model="formData.doctorsAppointmentDate"
               position="down"
               :label="'Datum des Arzttermins'"
               :placeholder="$t('general.date')"
               :validation="v$?.doctorsAppointmentDate"
               :messages="v$?.doctorsAppointmentDate?.required?.$message"/>
        </span>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <span>eAU</span>
        <div class="row">
          <BaseRadio
              v-model="formData.eAu"
              label="Ja"
              name="eAu"
              id="1"
              :labelPosition="'right'"
              @change="onEAuSentChange"
              classFormGroup="col-auto"/>

          <BaseRadio
              v-model="formData.eAu"
              label="Nein"
              name="eAu"
              id="0"
              :labelPosition="'right'"
              @change="onEAuSentChange"
              classFormGroup="col-auto"/>
        </div>

        <div v-if="formData.eAu === '1'">
          <span>AU an personal@bildung-sg.de versende</span>
          <div class="row">
            <BaseRadio
                v-model="formData.eAuSentToHr"
                label="Ja"
                name="eAuSentToHr"
                id="1"
                :labelPosition="'right'"
                classFormGroup="col-auto"/>

            <BaseRadio
                v-model="formData.eAuSentToHr"
                label="Nein"
                name="eAuSentToHr"
                id="0"
                :labelPosition="'right'"
                classFormGroup="col-auto"/>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12">
        <span>Folgebescheinigung</span>
        <div class="row">
          <BaseRadio
              v-model="formData.followUpCertificate"
              label="Ja"
              name="followUpCertificate"
              id="1"
              :labelPosition="'right'"
              classFormGroup="col-auto"/>

          <BaseRadio
              v-model="formData.followUpCertificate"
              label="Nein"
              name="followUpCertificate"
              id="0"
              :labelPosition="'right'"
              classFormGroup="col-auto"/>
        </div>
      </div>
    </div>
    <div class="d-flex justify-content-center w-100 mt-3">
      <button class="btn btn-primary" type="submit">{{ $t('general.button.save') }}</button>
    </div>
  </form>
</template>

<script setup>
import reportSickDaysService from '@/api/ReportSickDays';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import BaseRadio from '@/components/base/BaseRadio.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import { ErrorToast, SuccessToast } from '@/components/VueToast';
import { requiredRule } from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t }        = useI18n();
const isLoaded     = ref(false);
const isSubmitting = ref(false);
const emit         = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  reportedSickDaysId: {
    type: [ String, Number ],
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formData = ref({
  name: '',
  startDate: '',
  endDate: '',
  doctorsAppointmentMade: false,
  doctorsAppointmentDate: '',
  eAu: '0',
  eAuSentToHr: '0',
  followUpCertificate: '0',
});

const rules = ref({
  startDate: requiredRule(t),
  eAu: requiredRule(t),
});

const computedRules = computed(() => rules.value);

const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    formData.value.eAu                 = +formData.value.eAu;
    formData.value.eAuSentToHr         = +formData.value.eAuSentToHr;
    formData.value.followUpCertificate = +formData.value.followUpCertificate;

    if (props.reportedSickDaysId) {
      await reportSickDaysService.putSickDaysV2(formData.value, +props.reportedSickDaysId);
    } else {
      await reportSickDaysService.postSickDaysV2(formData.value);
    }

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

function onDoctorsAppointmentChange(value) {
  if (value === true) {
    rules.value = {
      ...rules.value,
      doctorsAppointmentDate: requiredRule(t),
    };
  } else {
    formData.value.doctorsAppointmentDate = '';

    rules.value = {
      ...rules.value,
      doctorsAppointmentDate: '',
    };
  }
}

function onEAuSentChange() {
  formData.value.eAuSentToHr = '0';
}

async function getReportedSickDaysData(id) {
  try {
    const { data }            = await reportSickDaysService.getReportedSickDaysDetailsV2(+id);
    const eAu                 = String(data.data.eAu);
    const eAuSentToHr         = String(data.data.eAuSentToHr);
    const followUpCertificate = String(data.data.followUpCertificate);

    formData.value = {
      ...data.data,
      eAu: eAu,
      eAuSentToHr: eAuSentToHr,
      followUpCertificate: followUpCertificate,
    };

  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  if (props.reportedSickDaysId) {
    await getReportedSickDaysData(props.reportedSickDaysId);
  }
  isLoaded.value = true;
});
</script>
<style scoped>

</style>